/**
 * useSmartDefaults — smart defaults storage hook.
 *
 * Phase 8 Prompt 3 — per audit deliverable 04D Pattern P11 + Section K.
 *
 * Pre-fills new-entity forms from operator history:
 *   - Preferred send time / timezone
 *   - Recent template / audience / funnel-template selections
 *   - Test recipient email
 *   - Audience size cap preference
 *
 * Storage backing (Prompt 3 scope): localStorage with key
 * `lifescribe_admin_smart_defaults_v1`. A future prompt swaps this to the
 * `user_admin_preferences` table via API (per Phase 7 §A.1 item A1-6).
 * The hook contract doesn't change — backing-store swap is internal.
 *
 * Usage:
 *   const { defaults, recordChoice, recordRecent, reset } = useSmartDefaults();
 *
 *   // In a new-campaign form:
 *   <DateTimePicker defaultValue={defaults.preferredSendTime} />
 *
 *   // After user picks a template:
 *   recordRecent('templateIds', template.id);
 *
 *   // After user clicks Send:
 *   recordChoice({ preferredSendTime: form.scheduledAt });
 */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "lifescribe_admin_smart_defaults_v1";
const MAX_RECENTS = 8;

export interface SmartDefaults {
  testRecipientEmail: string | null;
  preferredSendTime: string | null; // "HH:MM" (24h)
  preferredTimezone: string | null;
  preferredAudienceSizeCap: number | null;
  recentTemplateIds: string[];
  recentAudienceIds: string[];
  recentFunnelTemplateKeys: string[];
}

export type RecentListKey =
  | "recentTemplateIds"
  | "recentAudienceIds"
  | "recentFunnelTemplateKeys";

const EMPTY: SmartDefaults = {
  testRecipientEmail: null,
  preferredSendTime: null,
  preferredTimezone:
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
  preferredAudienceSizeCap: null,
  recentTemplateIds: [],
  recentAudienceIds: [],
  recentFunnelTemplateKeys: [],
};

function readFromStorage(): SmartDefaults {
  if (typeof window === "undefined") return EMPTY;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<SmartDefaults>;
    return { ...EMPTY, ...parsed };
  } catch {
    return EMPTY;
  }
}

function writeToStorage(value: SmartDefaults): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage might be full or disabled — fail silently
  }
}

export interface UseSmartDefaultsReturn {
  defaults: SmartDefaults;
  /** Merge new field values into the stored defaults (e.g. preferredSendTime). */
  recordChoice: (patch: Partial<SmartDefaults>) => void;
  /** Push an id onto a recent-list (deduplicated; max-N FIFO). */
  recordRecent: (key: RecentListKey, id: string) => void;
  /** Reset all defaults to empty (operator triggered from profile page). */
  reset: () => void;
  /** Whether the operator has any meaningful defaults yet. */
  hasAnyDefaults: boolean;
}

export function useSmartDefaults(): UseSmartDefaultsReturn {
  const [defaults, setDefaults] = useState<SmartDefaults>(readFromStorage);

  // Cross-tab sync via the "storage" event so defaults reflect changes made
  // on other tabs without a full page reload.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setDefaults(readFromStorage());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const recordChoice = useCallback((patch: Partial<SmartDefaults>) => {
    setDefaults((prev) => {
      const next = { ...prev, ...patch };
      writeToStorage(next);
      return next;
    });
  }, []);

  const recordRecent = useCallback((key: RecentListKey, id: string) => {
    if (!id) return;
    setDefaults((prev) => {
      const current = prev[key] ?? [];
      const filtered = current.filter((existing) => existing !== id);
      const updated = [id, ...filtered].slice(0, MAX_RECENTS);
      const next = { ...prev, [key]: updated };
      writeToStorage(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    writeToStorage(EMPTY);
    setDefaults(EMPTY);
  }, []);

  const hasAnyDefaults =
    defaults.testRecipientEmail !== null ||
    defaults.preferredSendTime !== null ||
    defaults.preferredAudienceSizeCap !== null ||
    defaults.recentTemplateIds.length > 0 ||
    defaults.recentAudienceIds.length > 0 ||
    defaults.recentFunnelTemplateKeys.length > 0;

  return {
    defaults,
    recordChoice,
    recordRecent,
    reset,
    hasAnyDefaults,
  };
}

/**
 * Sort an array of items so any whose id appears in `recentIds` floats to
 * the top in recency order. Useful for default-ordering template / audience
 * pickers.
 */
export function sortByRecency<T extends { id: string }>(
  items: T[],
  recentIds: string[],
): T[] {
  const indexMap = new Map<string, number>();
  recentIds.forEach((id, idx) => indexMap.set(id, idx));
  return [...items].sort((a, b) => {
    const aIdx = indexMap.get(a.id);
    const bIdx = indexMap.get(b.id);
    if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx;
    if (aIdx !== undefined) return -1;
    if (bIdx !== undefined) return 1;
    return 0;
  });
}
